import { Link } from "react-router-dom";
import { BuyBox, DropNotify } from "@/components/BuyBox";
import { trackSelectItem } from "@/lib/analytics";
import { useStockOverrides, applyStock, useDropDates } from "@/lib/stock";

const dropBadge = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d <= new Date()) return null;
  return `DROPS ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase()}`;
};

export const ProductCard = ({ p: raw, listId = "shop", listName = "Shop" }) => {
  const overrides = useStockOverrides();
  const dropDates = useDropDates();
  const p = applyStock(raw, overrides);
  if (p.is_service) {
    return (
      <article data-testid={`product-card-${p.slug}`} className="group bg-night flex flex-col">
        <Link to="/collabs" className="hover-zoom aspect-square relative block">
          <img src={p.image} alt="Club Collab Program — co-branded run club merch by Ninja Run Club" width="1024" height="1024" className="img-bw h-full w-full object-cover" loading="lazy" />
          <span className="absolute top-4 left-4 font-mono-label bg-white text-ink px-3 py-1.5">BY APPLICATION</span>
        </Link>
        <div className="p-6 border-t border-white/10 flex-1 flex flex-col">
          <h3 className="font-display text-xl lg:text-2xl">{p.name}</h3>
          <p className="text-white/50 text-sm mt-2">{p.description}</p>
          <Link to="/collabs" data-testid="collab-kit-link" className="font-mono-label link-underline text-white/60 mt-4 inline-block w-fit">APPLY VIA COLLABS →</Link>
        </div>
      </article>
    );
  }
  const url = `/products/${p.url_slug}`;
  const onClick = () => trackSelectItem(p, listId, listName);
  return (
    <article data-testid={`product-card-${p.slug}`} className="group bg-night flex flex-col">
      <Link to={url} onClick={onClick} data-testid={`product-link-${p.slug}`} aria-label={p.seo_name} className="aspect-square relative block overflow-hidden">
        <img src={p.image} alt={p.alt || `${p.name} — Ninja Run Club apparel`} width="1024" height="1024" className={`${p.color_art ? "" : "img-bw "}h-full w-full object-cover transition-opacity duration-500 group-hover:opacity-0`} loading="lazy" />
        <img
          src={p.image_hover || p.image}
          alt=""
          aria-hidden="true"
          width="1024"
          height="1024"
          loading="lazy"
          className={`${p.color_art ? "" : "img-bw "}absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100`}
          style={p.image_hover ? undefined : { transform: "scale(1.9)", transformOrigin: "50% 32%" }}
          data-testid={`product-detail-view-${p.slug}`}
        />
        {p.status !== "available" && (
          <span className="absolute top-4 left-4 font-mono-label bg-white text-ink px-3 py-1.5" data-testid={`product-badge-${p.slug}`}>
            {p.status === "preorder" ? "PREORDER" : dropBadge(dropDates[p.slug]) || "WAITLIST"}
          </span>
        )}
      </Link>
      <div className="p-6 border-t border-white/10 flex-1 flex flex-col">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-xl lg:text-2xl leading-tight">
            <Link to={url} onClick={onClick} className="hover:text-white/70 transition-colors duration-200">{p.name}</Link>
          </h3>
          <p className="font-mono-label text-white/60 shrink-0">{p.price ? `$${p.price}` : "—"}</p>
        </div>
        <p className="text-white/50 text-sm mt-2">{p.description}</p>
        <p className="font-mono-label text-white/30 mt-3">
          {p.status === "available" ? "IN STOCK" : "WAITLIST"} ·{" "}
          <Link to={url} onClick={onClick} data-testid={`product-details-${p.slug}`} className="underline underline-offset-4 hover:text-white transition-colors duration-200">DETAILS</Link>
        </p>
        <div className="mt-auto">
          {p.price && p.status === "available" ? (
            <BuyBox product={p} />
          ) : p.price || (p.collections || []).includes("City Series") ? (
            <DropNotify product={p} />
          ) : null}
        </div>
      </div>
    </article>
  );
};
